import { computed, nextTick } from 'vue'

import { useGarageStore } from '@/stores/garage.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { getI18n } from '@/plugins/i18n'

import * as dataGridColumn from '@/composables/data-grid-column'
import * as dataGrid from '@/composables/data-grid'
import { useLookup } from '@/composables/data-grid'
import { useSubjectService } from '@/services/subject.service'

export const useFormData = (selected, currentVehicleFuelType = {}, currentCustomerSubjectType = {}, currentCarService, formRef = {}, isOwnerSaveDisabled = {}, isVehicleSaveDisabled = {}, isReadOnly = {}, isSaveDisabled, infoBike = {}) => {
  const i18n = getI18n()
  const garageStore = useGarageStore()
  const registryStore = useRegistryStore()
  const webSiteStore = useWebSiteStore()
  const { isNameExtMandatory } = useSubjectService()

  const engineSizeLabel = computed(() =>
    currentVehicleFuelType.value === registryStore.vehicleFuelTypesEnum.ELECTRIC
      ? i18n.t('common.kiloWattAlt')
      : i18n.t('common.cubicCapacityAlt')
  )

  const hasVatNumber = computed(() => {
    const customer = currentCustomerSubjectType.value ? registryStore.getLocalizedSubjectTypes().find(x => x.id === currentCustomerSubjectType.value) : null
    return customer ? customer['has_vat_number'] : false
  })

  const setLabel = (trueLabel, falseLabel) => {
    return computed(() =>
      hasVatNumber.value ? i18n.t(trueLabel) : i18n.t(falseLabel)
    ).value
  }

  const disabledOwnerFields = ['subject_type_ref', 'subject_sub_type_ref', 'mobile', 'name']
  const disabledVehicleFields = [['plate_number', 'vin'], 'name', 'supplier_brand_ref']

  const validateForm = () => {
    const form = formRef.value?.instance
    if (!form) return false

    form.validate()
  }

  const getFilteredSupplierBrands = () => {
    const store = registryStore.supplierBrands
    return {
      store: store,
      sort: 'code',
      paginate: true,
    }
  }

  const toggleReadOnlyValidation = () => ({
    type: 'custom',
    message: i18n.t('common.required'),
    reevaluate: true,
    validationCallback: (e) => {
      const intervention_type_ref = formRef.value.formData.intervention_type_ref

      if (intervention_type_ref === garageStore.getLocalizedInterventionTypes().find(el => el.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE)?.id) {
        return e.value != null && e.value !== ''
      }

      return true
    }
  })

  const getSubjectTypes = () => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', null],
      sort: 'name',
    }
  }

  const getSubjectSubTypes = () => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', formRef.value?.formData?.customer?.subject_type_ref || 0],
      sort: 'name',
    }
  }

  const getCurrentHalfHour = () => {
    const now = new Date()
    now.setMinutes(now.getMinutes() < 30 ? 0 : 30, 0, 0)
    return now
  }

  const getValidMinimumDate = () => {
    if (!formRef.value?.formData?.id) { return getCurrentHalfHour() }
    const start = new Date(formRef.value?.formData?.start)
    return start < new Date() ? start : getCurrentHalfHour()
  }

  const validateFutureAppointmentDate = ({ value }) => {
    if (formRef.value?.formData?.id) return true

    return value && new Date(value) >= getCurrentHalfHour()
  }

  const futureAppointmentDateValidation = () => ({
    type: 'custom',
    message: i18n.t('warning.only_future_appointments_are_allowed'),
    reevaluate: true,
    validationCallback: validateFutureAppointmentDate,
  })

  const validatePlateNumberNoSpaces = (e) => {
    if (registryStore.companyData.vehicle_plate_allow_blank_spaces) {
      return true
    }

    const value = e.value || ''
    return !/\s/.test(value)
  }

  const infoBikeGroup = infoBike.infoBikeFormGroup?.(i18n, garageStore, 4)
  if (infoBikeGroup) {
    infoBikeGroup.items = infoBikeGroup.items.map(item => {
      if (item.dataField === 'infoBike.plate_number') {
        return { ...item, template: 'infoBikePlateNumberEditor' }
      }
      return item
    })
  }

  const brandColumn = dataGridColumn.useInfoBikeBrandColumn()
  const modelColumn = dataGridColumn.useInfoBikeModelColumn({ brand_code: infoBike.currentInfoBikeBrandCode })
  const variantColumn = dataGridColumn.useInfoBikeVariantColumn({ model_code: infoBike.currentInfoBikeModelCode })

  infoBikeGroup.items = infoBikeGroup.items.map(item => {
    if (item.dataField === 'infoBike.plate_number') {
      return { ...item, template: 'infoBikePlateNumberEditor', label: { text: ' '} }
    }
    if (item.dataField === 'infoBike.brand') {
      return {
        ...item,
        editorType: 'dxSelectBox',
        label: { text: i18n.t('common.brandAlt') },
        editorOptions: {
          ...brandColumn.lookup,
          searchEnabled: true,
          onValueChanged: infoBike.infoBikeCascade?.onBrandChanged
        }
      }
    }
    if (item.dataField === 'infoBike.model') {
      return {
        ...item,
        editorType: 'dxSelectBox',
        label: { text: i18n.t('common.model') },
        editorOptions: {
          ...modelColumn.lookup,
          onValueChanged: infoBike.infoBikeCascade?.onModelChanged,
          disabled: !infoBike.currentInfoBikeBrandCode?.value
        }
      }
    }
    if (item.dataField === 'infoBike.variant') {
      return {
        ...item,
        editorType: 'dxSelectBox',
        label: { text: i18n.t('common.variant') },
        editorOptions: {
          ...variantColumn.lookup,
          onValueChanged: infoBike.infoBikeCascade?.onVariantChanged,
          disabled: !infoBike.currentInfoBikeModelCode?.value
        }
      }
    }
    if (item.dataField === 'infoBike.infoBike') {
      return { ...item, label: { text: i18n.t('common.infoBike') }, editorOptions: { disabled: true } }
    }
    return item
  })

  return computed(() => [
    {
      itemType: 'group',
      cssClass: 'dx-form-group-no-background',
      colCount: 4,
      items: [
      {
        itemType: 'group',
        items: [
          {
            itemType: 'group',
            caption: i18n.t('common.appointment'),
            name: 'mdi-calendar-month-outline',
            captionTemplate: 'generic-caption-template',
            colCount: 4,
            items: [
              {
                dataField: 'start',
                label: { text: i18n.t('common.startDate') },
                colSpan: 4,
                editorType: 'dxDateBox',
                editorOptions: {
                  type: 'datetime',
                  min: getValidMinimumDate(),
                  disabled: isReadOnly.value,
                  onValueChanged: (e) => {
                    const oldStart = new Date(e.previousValue)
                    const oldEnd = new Date(formRef.value.formData.end)

                    const duration = oldEnd.getTime() - oldStart.getTime()

                    const newStart = new Date(e.value)
                    if (newStart > oldEnd && duration > 0) {
                      const newEnd = new Date(newStart.getTime() + duration)
                      formRef.value.formData.end = newEnd
                    }
                  },
                },
                validationRules: [futureAppointmentDateValidation()],
              },
              {
                dataField: 'end',
                label: { text: i18n.t('common.endDate') },
                colSpan: 4,
                editorType: 'dxDateBox',
                editorOptions: {
                  type: 'datetime',
                  min: formRef.value?.formData?.start,
                  disabled: isReadOnly.value,
                },
                validationRules: [futureAppointmentDateValidation()],
              },
              {
                dataField: 'delivery_datetime',
                label: { text: i18n.t('common.expectedDeliveryDate') },
                colSpan: 2,
                editorType: 'dxDateBox',
                editorOptions: {
                  type: 'datetime',
                  min: formRef.value?.formData?.end
                },
              },
              {
                dataField: 'on_site_waiting',
                label: { text: i18n.t('common.onSiteWaiting') },
                colSpan: 2,
                editorType: 'dxCheckBox',
                cssClass: 'on-site-waiting-item',
              },
              {
                dataField: 'garage_reason_ref',
                label: { text: i18n.t('common.workReason') },
                colSpan: 4,
                editorType: 'dxSelectBox',
                editorOptions: {
                  ...useLookup({ store: garageStore.getLocalizedGarageReasons() }),
                  searchEnabled: true,
                  onValueChanged: async(e) => {
                    const value = e.value

                    const currentGarageReason = garageStore.garageReasons.find(i => i.id === value)
                    const interventionType = garageStore.interventionTypes.find(i => i.id === currentGarageReason?.intervention_type_ref)

                    formRef.value.formData.intervention_type_ref = currentGarageReason?.intervention_type_ref

                    const hasCarService = interventionType?.has_car_service
                    const isCarService = interventionType?.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE
                    formRef.value.instance.getEditor('car_service')?.option('disabled', !hasCarService)

                    if (!hasCarService || !isCarService) {
                      formRef.value.formData.car_service = null
                      formRef.value?.instance?.repaint()
                      validateForm()
                      return
                    }

                    const vehicleId = formRef.value.formData?.vehicle?.id

                    if (vehicleId) {
                      const res = await registryStore.api.getVehicleNextCarServiceAsync(vehicleId)
                      formRef.value.formData.car_service = currentCarService.value = res.data
                    } else {
                      formRef.value.formData.car_service = null
                    }

                    formRef.value?.instance?.repaint()
                    validateForm()
                  },
                },
                validationRules: [{ type: 'required' }],
              },
              {
                dataField: 'intervention_type_ref',
                label: { text: i18n.t('common.interventionType') },
                colSpan: 2,
                template: 'interventionTypeCellTemplate',
                editorOptions: { readOnly: true },
              },
              {
                label: { text: i18n.t('common.carServiceNumber') },
                dataField: 'car_service',
                colSpan: 2,
                dataType: 'number',
                template: 'carServiceNumberEditor',
                validationRules: [toggleReadOnlyValidation('car_service')],
              },
              dataGridColumn.useDepositColumn({
                label: { text: i18n.t('common.deposit') },
                colSpan: 2,
                dataField: 'deposit',
                template: 'depositEditor',
                editorType: 'dxSelectBox',
                visible: true,
              }),
            ]
          },
          {
            itemType: 'group',
            caption: i18n.t('common.mechanic'),
            name: 'mdi-hammer-screwdriver',
            captionTemplate: 'mechanic-info-caption-template',
            colCount: 4,
            items: [
              {
                dataField: 'mechanics',
                label: { text: i18n.t('common.mechanic') },
                colSpan: 4,
                template: 'mechanicsEditor',
                validationRules: [{ type: 'required' }],
              },
            ]
          },
          {
            itemType: 'group',
            caption: i18n.t('common.courtesyVehicle'),
            name: 'mdi-motorbike',
            captionTemplate: 'generic-caption-template',
            colCount: 4,
            items: [
              dataGridColumn.useVehicleColumn({
                label: { text: i18n.t('common.vehicle') },
                colSpan: 4,
                dataField: 'courtesy_vehicle',
                template: 'courtesyVehicleEditor',
                editorType: 'dxSelectBox',
                validationRules: []
              }),
            ]
          },
        ]
      },
      {
        itemType: 'tabbed',
        colSpan: 3,
        tabs: [
          {
            title: i18n.t('common.mainInfo'),
            colCount: 4,
            items: [
              infoBikeGroup,
              {
                itemType: 'group',
                caption: i18n.t('common.vehicleInfo'),
                name: 'mdi-motorbike',
                captionTemplate: 'vehicle-info-caption-template',
                validationGroup: 'vehicleGroup',
                colSpan: 2,
                colCount: 4,
                items: [
                  dataGridColumn.useVehicleColumn({
                    label: { text: i18n.t('common.vehicle') },
                    colSpan: 2,
                    dataField: 'vehicle_id',
                    template: 'vehicleEditor',
                    editorType: 'dxSelectBox',
                  }),
                  { itemType: 'empty', colSpan: 2 },
                  {
                    dataField: 'vehicle.plate_number',
                    label: { text: i18n.t('common.plate') },
                    colSpan: 2,
                    editorOptions: {
                      buttons: [dataGridColumn.useButtonsInfoTooltipColumn()],
                      onValueChanged: (e) => {
                        const value = e.value

                        isVehicleSaveDisabled.value = isSaveDisabled('vehicle', !!formRef.value?.formData?.vehicle_id, disabledVehicleFields)

                        if (value && formRef.value?.formData.vehicle) {
                          formRef.value.formData.vehicle.plate_number = value.toUpperCase()
                          if (!registryStore.companyData.vehicle_plate_allow_blank_spaces) {
                            formRef.value.formData.vehicle?.plate_number?.replace(/\s+/g, '')
                          }
                          formRef.value.formData.vehicle.plate_number = value.slice(0, 15)
                        }
                      }
                    },
                    validationRules: [
                      {
                        type: 'custom',
                        reevaluate: true,
                        validationCallback: validatePlateNumberNoSpaces,
                        message: i18n.t('error.validation.noBlankSpacesAllowed'),
                      },
                      {
                        type: 'custom',
                        reevaluate: true,
                        validationCallback: function(e) {
                          const vin = formRef.value?.formData?.vehicle?.vin
                          if (!e.value && !vin) return false
                          return true
                        },
                        message: i18n.t('common.vinOrPlateRequired'),
                      },
                    ],
                  },
                  dataGridColumn.useVinColumn({
                    dataField: 'vehicle.vin',
                    label: { text: i18n.t('common.vin') },
                    colSpan: 2,
                    template: 'serviceHistoryRef',
                    validationRules: [
                      {
                        type: 'custom',
                        reevaluate: true,
                        validationCallback: function(e) {
                          const plate = formRef.value?.formData?.vehicle?.plate_number
                          if (!e.value && !plate) return false
                          return true
                        },
                        message: i18n.t('common.vinOrPlateRequired'),
                      },
                    ],
                  }),
                  {
                    dataField: 'vehicle.supplier_brand_ref',
                    label: { text: i18n.t('common.supplierBrand') },
                    colSpan: 2,
                    editorType: 'dxSelectBox',
                    editorOptions: {
                      ...useLookup({ dataSource: getFilteredSupplierBrands().store}),
                      searchEnabled: true,
                      onValueChanged: (e) => {
                        isVehicleSaveDisabled.value = isSaveDisabled('vehicle', !!formRef.value?.formData?.vehicle_id, disabledVehicleFields)
                      }
                    },
                    validationRules: [{ type: 'required' }],
                  },
                  {
                    dataField: 'vehicle.name',
                    label: { text: i18n.t('common.description') },
                    colSpan: 2,
                    editorOptions: {
                      maxLength: 50,
                      onValueChanged: (e) => {
                        isVehicleSaveDisabled.value = isSaveDisabled('vehicle', !!formRef.value?.formData?.vehicle_id, disabledVehicleFields)
                      }
                    },
                    validationRules: [{ type: 'required' }]
                  },
                  { dataField: 'vehicle.color', label: { text: i18n.t('common.color') }, colSpan: 2, editorOptions: { maxLength: 50 } },
                  {
                    dataField: 'km',
                    editorType: 'dxNumberBox',
                    label: { text: i18n.t('common.km') },
                    editorOptions: {
                      min: 0,
                      disabled: isReadOnly.value,
                      onValueChanged: (e) => {
                        isVehicleSaveDisabled.value = isSaveDisabled('vehicle', !!formRef.value?.formData?.vehicle_id, disabledVehicleFields)
                      }
                    },
                    validationRules: [
                      {
                        type: 'custom',
                        reevaluate: true,
                        message: i18n.t('error.validation.greater_value'),
                        validationCallback: (e) => {
                          const vehicleId = formRef.value?.formData?.vehicle_id
                          const previousKm = registryStore.vehicles.find(v => v.id === vehicleId)?.km

                          if (vehicleId && previousKm != null && e.value < previousKm) {
                            return false
                          }
                          return true
                        },
                      },
                      {
                        type: 'custom',
                        message: i18n.t('common.required'),
                        reevaluate: true,
                        validationCallback: (e) => {
                          const intervention_type_ref = formRef.value?.formData?.intervention_type_ref

                          if (intervention_type_ref === garageStore.getLocalizedInterventionTypes().find(el => el.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE)?.id) {
                            return e.value != null && e.value !== ''
                          }

                          return true
                        }
                      },
                    ],
                  },
                  { dataField: 'vehicle.engine_size', editorType: 'dxNumberBox', label: { text: engineSizeLabel.value }, editorOptions: { min: 0 }},
                ]
              },
              {
                itemType: 'group',
                caption: i18n.t('common.ownerInfo'),
                name: 'mdi-account-circle',
                captionTemplate: 'owner-info-caption-template',
                validationGroup: 'ownerGroup',
                colSpan: 2,
                colCount: 4,
                items: [
                  dataGridColumn.useCustomerColumn({
                    label: { text: i18n.t('common.owner') },
                    dataField: 'customer_id',
                    colSpan: 2,
                    template: 'customerEditor',
                    editorType: 'dxSelectBox',
                  }),
                  { itemType: 'empty', colSpan: 2 },
                  {
                    dataField: 'customer.subject_type_ref',
                    label: { text: i18n.t('common.personType') },
                    validationRules: [{ type: 'required' }],
                    colSpan: 2,
                    editorType: 'dxSelectBox',
                    editorOptions: {
                      ...useLookup({ dataSource: getSubjectTypes() }),
                      onValueChanged: (e) => {
                        if (formRef.value?.formData?.customer && !formRef.value?.formData?.customer_id) {
                          formRef.value.formData.customer.subject_sub_type_ref = null
                        }
                        isOwnerSaveDisabled.value = isSaveDisabled('customer', !!formRef.value?.formData?.customer_id, disabledOwnerFields)
                        currentCustomerSubjectType.value = formRef.value?.formData?.customer?.subject_type_ref
                        if (e.event) {
                          nextTick(() => formRef.value?.instance?.getEditor('customer.subject_type_ref')?.focus())
                        }
                      }
                    },
                  },
                  {
                    dataField: 'customer.subject_sub_type_ref',
                    label: { text: setLabel('common.category', 'common.gender') },
                    validationRules: [{ type: 'required' }],
                    colSpan: 2,
                    editorType: 'dxSelectBox',
                    editorOptions: {
                      ...useLookup({ dataSource: getSubjectSubTypes() }),
                      onValueChanged: (e) => {
                        isOwnerSaveDisabled.value = isSaveDisabled('customer', !!formRef.value?.formData?.customer_id, disabledOwnerFields)
                      }
                    },
                  },
                  {
                    dataField: 'customer.name',
                    label: { text: setLabel('common.nameAlt', 'common.lastName') },
                    validationRules: [{ type: 'required' }],
                    colSpan: 2,
                  },
                  {
                    dataField: 'customer.name_ext',
                    label: { text: `${setLabel('common.nameExtAlt', 'common.firstName')}${isNameExtMandatory(currentCustomerSubjectType.value) ? ' *' : ''}` },
                    colSpan: 2,
                    validationRules: [{
                      type: 'custom',
                      reevaluate: true,
                      message: i18n.t('common.required'),
                      validationCallback: (e) => !isNameExtMandatory(formRef.value?.formData?.customer?.subject_type_ref) || !!e.value,
                    }],
                    editorOptions: {
                      onValueChanged: (e) => {
                        isOwnerSaveDisabled.value = isSaveDisabled('customer', !!formRef.value?.formData?.customer_id, disabledOwnerFields)
                      }
                    },
                  },
                  {
                    dataField: 'customer.email',
                    label: { text: i18n.t('common.email') },
                    colSpan: 2,
                    validationRules: [{ type: 'email' }],
                  },
                  {
                    dataField: 'customer.phone',
                    label: { text: i18n.t('common.phone') },
                    colSpan: 2,
                    editorOptions: {
                      onContentReady: (e) => { dataGrid.usePhoneMask(e.element, { definitions: dataGrid.useMask().mobileMaskRules }) },
                    }
                  },
                  {
                    dataField: 'customer.mobile',
                    label: { text: i18n.t('common.mobile') },
                    colSpan: 2,
                    validationRules: [{ type: 'required' }],
                    editorOptions: {
                      onContentReady: (e) => { dataGrid.usePhoneMask(e.element, { definitions: dataGrid.useMask().mobileMaskRules }) },
                      onValueChanged: (e) => {
                        isOwnerSaveDisabled.value = isSaveDisabled('customer', !!formRef.value?.formData?.customer_id, disabledOwnerFields)
                      }
                    },
                  },
                  // US1340
                  // {
                  //   dataField: 'appointment_notify',
                  //   label: { text: i18n.t('common.appointmentNotify') },
                  //   colSpan: 2,
                  //   template: 'appointmentNotifyEditor',
                  // },
                ]
              },
              {
                itemType: 'group',
                caption: i18n.t('common.mechanicNotes', 2),
                name: 'mdi-comment-account',
                captionTemplate: 'generic-caption-template',
                colSpan: 2,
                items: [
                  {
                    dataField: 'mechanic_notes',
                    label: { text: i18n.t('common.mechanicNotes') },
                    colSpan: 2,
                  },
                ]
              },
              {
                itemType: 'group',
                caption: i18n.t('common.customerNotes', 2),
                name: 'mdi-notebook-edit',
                captionTemplate: 'generic-caption-template',
                colSpan: 2,
                items: [
                  {
                    dataField: 'customer_notes',
                    label: { text: i18n.t('common.customerNotes') },
                    colSpan: 2,
                  },
                ]
              },
            ]
          },
          //  --- US 623 https://dev.azure.com/softway-it/ArcoWeb/_workitems/edit/623
          // {
          //   title: i18n.t('common.processes'),
          //   colCount: 4,
          //   items: [
          //     {
          //       colSpan: 4,
          //       template: 'productGridTemplate',
          //     },
          //   ]
          // },
          {
            title: i18n.t('common.additionalInfo'),
            colCount: 4,
            items: [
              {
                dataField: 'key_number',
                label: { text: i18n.t('common.keyNumber') },
                colSpan: 2,
              },
              {
                dataField: 'parking_number',
                label: { text: i18n.t('common.parkingNumber') },
                colSpan: 2,
              },
            ]
          },
          {
            title: i18n.t('common.linkedDocumentAlt', 2),
            colCount: 4,
            items: [
              {
                label: { text: i18n.t('common.worksheet') },
                template: 'worksheetDocumentTemplate',
              },
              {
                label: { text: i18n.t('common.saleDocument') },
                template: 'saleDocumentTemplate',
              },
            ]
          },
          {
            title: i18n.t('common.serviceHistory'),
            colCount: 4,
            items: [
              {
                colSpan: 4,
                template: 'serviceHistoryTemplate',
              },
            ]
          },
        ]
      }
      ]
    }
  ])
}
